import { useState, useLayoutEffect, useEffect } from 'react'

import { AvcTasksTaskIdDeleteRequest, AvcTaskPaginatedList } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery, useInfiniteQuery, useMutationState, InfiniteData, useQueryClient } from '@tanstack/react-query'
import { VirtualItem } from '@tanstack/react-virtual'

import { avcTasksOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks'
import { workspaceQueryKeys, avcTaskQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'
import { mergeBatchIntoSource } from 'utils'

type UseAsyncContentCaching = {
  virtualItems?: VirtualItem[]
  setLoadingRows: (rows: string[]) => void
}

export const useAsyncContentCaching = ({ virtualItems, setLoadingRows }: UseAsyncContentCaching) => {
  const queryClient = useQueryClient()

  const { workspaceId } = avcTasksOverviewRoute.useParams()
  const { query } = avcTasksOverviewRoute.useSearch()

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))

  const avcTasksQuery = useInfiniteQuery({
    ...avcTaskQueryKeys.list({
      repositoryId: workspaceQuery.data?.contentRepositoryId as string,
      query,
      view: 'Full',
    }),
    enabled: workspaceQuery.isSuccess && !!workspaceQuery.data.contentRepositoryId,
    select: (data) => ({
      ...data,
      flat: data?.pages.flatMap((page) => page.items),
    }),
    staleTime: Infinity,
    notifyOnChangeProps: 'all',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const [batchGetEnabled, setBatchGetEnabled] = useState(avcTasksQuery.isSuccess)

  const pendingAvcTasks = useMutationState({
    filters: { mutationKey: ['content', 'avcTasks', 'DELETE'], status: 'pending' },
    select: (mutation) => mutation.state.variables as AvcTasksTaskIdDeleteRequest,
  })

  const avcTaskBatchQuery = useQuery({
    ...avcTaskQueryKeys.batchGet(
      {
        view: 'Full',
      },
      () => virtualItems?.map((virtualItem) => avcTasksQuery.data?.flat[virtualItem.index]?.id) as string[],
    ),
    enabled: batchGetEnabled,
    refetchInterval: RefetchIntervals.FAST,
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  useLayoutEffect(() => {
    if (avcTasksQuery.isSuccess && avcTaskBatchQuery.isSuccess) {
      queryClient.setQueriesData<InfiniteData<AvcTaskPaginatedList>>({ queryKey: avcTaskQueryKeys.lists() }, (old) => {
        if (old) {
          return {
            ...old,
            pages:
              old.pages.map((page) => ({
                ...page,
                items: mergeBatchIntoSource(
                  page.items,
                  avcTaskBatchQuery.data.items,
                  new Set(avcTaskBatchQuery.data.notFoundIds),
                ),
              })) ?? [],
          }
        }
        return old
      })
    }
  }, [
    avcTaskBatchQuery.data?.items,
    avcTaskBatchQuery.data?.notFoundIds,
    avcTaskBatchQuery.isSuccess,
    avcTasksQuery.isSuccess,
    queryClient,
  ])

  useLayoutEffect(() => {
    setLoadingRows([...new Set([...pendingAvcTasks.map((task) => task.taskId)])])
  }, [pendingAvcTasks, setLoadingRows])

  useEffect(() => {
    if (avcTasksQuery.isSuccess) {
      if (!batchGetEnabled) {
        const timeoutHandle = setTimeout(() => {
          setBatchGetEnabled(true)
          clearTimeout(timeoutHandle)
        }, 5000)
      }
    }
  }, [avcTasksQuery.isSuccess, batchGetEnabled, queryClient])

  return {
    workspaceQuery,
    avcTasksQuery,
    setBatchGetEnabled,
  }
}
